// Clip pages (/clip/:id), the clip feed (/feed, signed in) and a game's clips on its page.

import { api } from "../api.ts";
import { getCatalogue, gameById } from "../catalogue.ts";
import {
  clipCard,
  clipError,
  clipVideo,
  CLIP_ID,
  degradedNotes,
  fmtAgo,
  fmtLength,
  fmtMoment,
  playThis,
  reportButton,
  type Clip,
  type ClipGame,
  type FeedPage,
} from "../clips.ts";
import { clear, errorBox, h, link, section } from "../dom.ts";
import { setTitle } from "../router.ts";
import { currentMe, signedIn } from "../session.ts";
import { nameSpan, quietSection } from "./game.ts";

export async function clipPage(params: Record<string, string>): Promise<HTMLElement> {
  const id = params.id ?? "";
  setTitle("Clip");
  const wrap = h("article", { class: "clip-page" });
  if (!CLIP_ID.test(id)) {
    clear(wrap, h("div", { class: "empty" }, h("h1", null, "Clip not found"), h("p", { class: "dim" }, "That isn't a clip link."), link("/", { class: "btn" }, "Home")));
    return wrap;
  }
  let r: { clip: Clip; game: ClipGame };
  try {
    r = await api<{ clip: Clip; game: ClipGame }>("community", `/clips/${encodeURIComponent(id)}`);
  } catch (e) {
    clear(wrap, h("div", { class: "empty" }, h("h1", null, "Clip not available"), h("p", { class: "dim" }, clipError(e)), link("/", { class: "btn" }, "Home")));
    return wrap;
  }
  const { clip, game } = r;
  // the catalogue title is the fallback when community couldn't reach accounts
  const cat = await getCatalogue().catch(() => null);
  const g = cat ? gameById(cat, game.id) : null;
  const title = game.title ?? g?.title ?? null;
  setTitle(clip.title || (title ? `Clip from ${title}` : "Clip"));
  const me = currentMe();
  const mine = me?.id === clip.uploader_id;
  const length = fmtLength(clip.duration_seconds);
  clear(
    wrap,
    h("div", { class: "clip-stage" }, clipVideo(clip, { autoplay: false })),
    h(
      "div",
      { class: "clip-info" },
      h(
        "div",
        { class: "clip-info-main" },
        h("h1", null, clip.title || (title ? `A moment in ${title}` : "A clip")),
        h(
          "p",
          { class: "dim" },
          "From ",
          link(`/g/${game.id}`, null, title ?? "this game"),
          ` at ${fmtMoment(clip.moment_seconds)} into the session`,
          clip.version ? ` · v${clip.version}` : null,
          length ? ` · ${length}` : null,
        ),
        h("p", { class: "dim small" }, "Shared by ", mine ? "you" : nameSpan(clip.uploader_id), ` · ${fmtAgo(clip.created_at)}`),
        clip.status === "hidden" ? h("p", { class: "note small" }, "This clip is hidden by moderators. Only you and moderators can see it.") : null,
      ),
      h(
        "div",
        { class: "clip-info-side" },
        playThis(game.id, title, "big"),
        h("p", { class: "dim small" }, "Opens the game in the Banger app. ", link(`/download?game=${encodeURIComponent(game.id)}`, null, "Get Banger")),
        mine ? null : reportButton(clip),
      ),
    ),
    gameClips(game.id, title, clip.id),
  );
  return wrap;
}

/** More clips of the same game (or all of them, on the game page). */
export function gameClips(gameId: string, title: string | null, except: string | null = null, heading = "More from this game", hideEmpty = false): HTMLElement {
  const load = async (body: HTMLElement) => {
    const r = await api<{ clips: Clip[]; next_cursor: string | null }>("community", `/games/${encodeURIComponent(gameId)}/clips?limit=7`);
    const list = r.clips.filter((c) => c.id !== except).slice(0, 6);
    if (!list.length) {
      if (!hideEmpty) clear(body, h("p", { class: "dim" }, except ? "No other clips yet." : "No clips yet. Save one in the Banger app while you play."));
      return false;
    }
    clear(body, h("ul", { class: "clip-grid" }, list.map((c) => clipCard(c, { id: gameId, title, play: "" }))));
    return true;
  };
  // a game page shows its clips only once there are some (wave 12); a clip page keeps its "No other clips yet"
  return hideEmpty ? quietSection(heading, load) : section(heading, async (body) => void (await load(body)));
}

export function feedPage(): HTMLElement {
  setTitle("Your feed");
  if (!signedIn()) {
    return h(
      "div",
      { class: "empty" },
      h("h1", null, "Your feed"),
      h("p", { class: "lead" }, "Clips from your friends, the games you follow and the creators you follow."),
      link(`/signin?next=${encodeURIComponent("/feed")}`, { class: "btn" }, "Sign in"),
    );
  }
  const list = h("ul", { class: "clip-grid" });
  const notes = h("div", { "aria-live": "polite" });
  const more = h("div", { class: "feed-more" });
  const seenNotes = new Set<string>();
  let shown = 0;
  const load = async (cursor: string | null) => {
    clear(more, h("p", { class: "dim" }, "Loading…"));
    let page: FeedPage;
    try {
      page = await api<FeedPage>("community", `/feed/clips?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`);
    } catch (e) {
      clear(more, errorBox(e));
      return;
    }
    for (const n of degradedNotes(page.degraded ?? [])) {
      if (seenNotes.has(n)) continue;
      seenNotes.add(n);
      notes.append(h("p", { class: "note small", role: "status" }, n));
    }
    for (const it of page.items) list.append(clipCard(it.clip, it.game, { reasons: it.reasons }));
    shown += page.items.length;
    // a page can be short (blocked clips are filtered out) while there is more: keep the button while there's a cursor
    if (page.next_cursor) {
      const next = page.next_cursor;
      clear(more, h("button", { type: "button", class: "btn ghost", onclick: () => void load(next) }, "Show more"));
    } else if (shown === 0) {
      clear(
        more,
        h(
          "div",
          { class: "panel" },
          h("h2", null, "Nothing here yet"),
          h("p", null, "Follow games and creators, or add friends in the Banger app, and their clips show up here."),
          link("/browse", { class: "btn" }, "Find games to follow"),
        ),
      );
    } else clear(more, h("p", { class: "dim small" }, "You're all caught up."));
  };
  void load(null);
  return h(
    "div",
    { class: "feed" },
    h("h1", null, "Your feed"),
    h("p", { class: "lead" }, "Clips from your friends, the games you follow and the creators you follow. Press Play this to jump into the game."),
    notes,
    list,
    more,
  );
}

